// M004-F0744 (docs/design/M004-F0744-F0747-Envelope-Workspace.md sections 1-5, 10) - the My Envelopes workspace: the envelope sidebar
// (Unsorted pinned), the receipt grid/list with running totals, filter and search, and the inline OPTIMISTIC "New envelope".
//
// Create (section 4): type + Enter and the row is there at once, selected, "Creating..." - no spinner, no modal. The client mints a
// `tmp-<uuid>` row key and the request's Idempotency-Key. The answer replaces the temp row BY envelope_id under the SAME React key
// (no flicker). A repeat Enter with the same text during flight is ignored. A network failure is retried once with the SAME key; a
// refusal (or a second network failure) rolls the row back, restores the typed text and focus, and shows the server's sentence and
// its code under the input. Nothing else moves.
//
// States (section 10): skeletons while loading; the empty card; the enlarged drop zone; the saved copy of the last good read (per
// user and organisation, browser storage) under a "Ledger is not reachable" banner with every write disabled and a retry on focus
// and every 30 s (owner ruling: keep the cached view when the Ledger is down); errors show the sentence + code + Try again and a
// failed open never leaves a spinner (WebUI #163).
//
// Move receipts (M004-F0745, section 6, #184): drag a tile (or the selection) onto a sidebar row, or select and use Move to… / M, or long-press
// and use the bottom sheet. The move is OPTIMISTIC and is sent at once (never held back for the Undo window): the tiles leave, both rows'
// counts and totals follow, a 6 s toast offers Undo (button, Z, Ctrl+Z). A refusal or a network failure puts the tiles back, shows the
// Ledger's sentence and code, and re-reads both lists. A multi-receipt move is all-or-nothing (the Ledger refuses the whole request).
//
// Submit a claim (M004-F0747, section 8.2, #186): the claimant's flow lives in useClaimFlow.js (panel, banner, the reviewer's reason, the
// ack, the "needs attention" count); this file wires it to the pane. Seam for the next ticket (no stub): upload progress/OCR (#185) extends
// EnvelopeUploadZone and ReceiptDrawer.
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../context/AuthContext';
import { envelopeApi, STILL_PROCESSING_CODE } from '../../services/envelopeApi';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import EnvelopeDetailsDrawer from './EnvelopeDetailsDrawer';
import EnvelopePane from './EnvelopePane';
import { duplicateReceiptIds, duplicatePlace } from './envelopeUploadModel';
import EnvelopeSidebar from './EnvelopeSidebar';
import MoveToast from './MoveToast';
import ReceiptDrawer from './ReceiptDrawer';
import ReceiptMoveMenu from './ReceiptMoveMenu';
import useOcrPolling from './useOcrPolling';
import { needsPolling, pollSignature } from './ocrModel';
import useClaimFlow from './useClaimFlow';
import {
  ENVELOPES_TAB_COPY, extractEnvelopeError, formatBytes, formatStamp, isEnvelopeNotFound, newIdempotencyKey, sortReceipts,
} from './envelopeModel';
import {
  CREATE_RETRY_DELAY_MS, NO_ENVELOPE_RETURNED_TEXT, OFFLINE_RETRY_MS, ONLINE_REFRESH_MS, addTemp, detailIsStale, defaultCurrencyFor, filterReceipts, formatSavedAt,
  isUnreachable, makeTempEnvelope, newClientKey, normaliseEnvelope, orderEnvelopes, readCache, readView, receiptHeadline, reconcileCreate,
  rollbackCreate, rowKey, totalsFor, writeCache, writeView,
} from './envelopeWorkspaceModel';
import {
  LIMIT_TEXT, MOVE_LIMIT, OFFLINE_HINT, alreadyThereSentence, applyMoveToDetails, applyMoveToRecords, envelopeLabel, giveReason, mergeMoveSide,
  moveSentence, moveTargets, nextFocusId, rangeIds, takeReason, undoneSentence, STALE_MOVE_CODE, MOVED_MOVE_CODE, staleMoveSentence,
} from './envelopeMoveModel';
import { DRAG_TYPE, buildGhost, removeGhost } from './receiptDrag';
import './EnvelopesWorkspace.css';

const unwrap = (response) => response?.data?.data || {};
const delay = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const OFFLINE_REASON = 'Ledger is not reachable — changes are off until it is back.';
const NO_FILTER = { query: '', status: 'all', ids: null };
const NO_SELECTION = { ids: [], anchor: null };
const RESTORE_FLASH_MS = 900;
const REFUSAL_FLASH_MS = 2500;
const isNarrowViewport = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 719px)').matches;

// Merge a record the Ledger sent into the list entry for the same envelope, keeping the row key (and so the DOM node).
const mergeRecord = (records, record) => records.map((item) => (
  item.envelope_id === record.envelope_id ? { ...item, ...normaliseEnvelope({ ...item, ...record }), clientKey: item.clientKey, pending: false } : item
));

export default function EnvelopesWorkspace({
  organisations = [], organisationsLoading = false, envelopeId = null, onOpenEnvelope, onDismissEnvelope: _onDismissEnvelope, onAttentionCount,
}) {
  const auth = useContext(AuthContext);
  const userKey = auth?.user?.id ?? auth?.user?.uuid ?? 'anon';
  // A real user replaced by ANOTHER real user resets the workspace (never show one member's envelopes to the next); the auth user
  // arriving late ('anon' -> id) is not a change of member and must not reset an in-flight create.
  const memberRef = useRef(null);
  const [memberEpoch, setMemberEpoch] = useState(0);
  useEffect(() => {
    if (userKey === 'anon') return;
    if (memberRef.current !== null && memberRef.current !== userKey) setMemberEpoch((epoch) => epoch + 1);
    memberRef.current = userKey;
  }, [userKey]);
  const [orgId, setOrgId] = useState('');
  const [listState, setListState] = useState({ status: 'loading', error: null });
  const [envelopes, setEnvelopes] = useState([]);
  const [details, setDetails] = useState({});
  const [offline, setOffline] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [filter, setFilter] = useState(NO_FILTER);
  const [view, setView] = useState(readView);
  const [text, setText] = useState('');
  const [createError, setCreateError] = useState(null);
  const [focusTick, setFocusTick] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  const [ocrAnnouncement, setOcrAnnouncement] = useState(''); // its own live region: an OCR change must not overwrite a move / refusal sentence
  const [openReceiptId, setOpenReceiptId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [purge, setPurge] = useState({ open: false, rows: [], error: '' });

  const loadedFor = useRef('');
  const offlineRef = useRef(null);
  const inputRef = useRef(null);
  const inflight = useRef(new Set());
  const failedKey = useRef(null);
  const listSeq = useRef(0);
  // M004-F0794: a detail sequence per envelope, and a mutation epoch (bumped when a local move, delete, create or claim completes): a
  // list/detail answer whose request started under an older epoch is dropped and asked again, never applied.
  const detailSeq = useRef({});
  const mutationEpoch = useRef(0);
  const loadedStamp = useRef({}); // envelope_id -> the updated_at the open detail was last loaded at (M004-F0793)
  const busyKeys = useRef(new Set()); // upload / drawer edit in flight (M004-F0793 d)
  const movesInFlight = useRef(0);
  const pendingRefresh = useRef('');
  const runRefreshRef = useRef(null);
  const loadDetailRef = useRef(null);
  const orgRef = useRef('');
  const loadListRef = useRef(null); // the org reset reads loadList through this ref, so it re-runs only when the organisation changes
  const selectedRef = useRef(null);
  const envelopesRef = useRef([]);
  const textRef = useRef('');
  const detailsRef = useRef({});
  const openerRef = useRef(null);
  envelopesRef.current = envelopes;
  selectedRef.current = selectedKey;
  textRef.current = text;
  detailsRef.current = details;
  orgRef.current = orgId;
  offlineRef.current = offline;

  const say = useCallback((message) => setAnnouncement(message), []);

  // ---- organisation ---------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!organisations.length) return;
    if (!organisations.some((org) => String(org.id) === orgId)) setOrgId(String(organisations[0].id));
  }, [organisations, orgId]);

  // ---- the list -------------------------------------------------------------------------------------------------------------------
  const applyOffline = useCallback((forOrg) => {
    const cache = readCache(userKey, forOrg);
    if (!cache) return false;
    setEnvelopes(orderEnvelopes(cache.envelopes));
    setDetails((current) => {
      const seeded = { ...current };
      Object.entries(cache.receipts || {}).forEach(([id, receipts]) => {
        seeded[id] = { status: 'ready', receipts: sortReceipts(receipts), error: null, cached: true };
      });
      return seeded;
    });
    setOffline({ savedAt: cache.savedAt });
    setListState({ status: 'ready', error: null });
    return true;
  }, [userKey]);

  const loadList = useCallback(async (forOrg, { silent = false, refreshOpen = false } = {}) => {
    if (!forOrg) return null;
    listSeq.current += 1;
    const seq = listSeq.current;
    const epoch = mutationEpoch.current;
    if (!silent) setListState((current) => (current.status === 'ready' ? current : { status: 'loading', error: null }));
    try {
      const data = unwrap(await envelopeApi.list(forOrg));
      if (seq !== listSeq.current || orgRef.current !== forOrg) return null;
      if (epoch !== mutationEpoch.current) return loadList(forOrg, { silent: true, refreshOpen }); // started before a local mutation completed: stale
      const records = Array.isArray(data.envelopes) ? data.envelopes : [];
      setEnvelopes((current) => {
        const temps = current.filter((item) => item.pending);
        const fresh = orderEnvelopes(records);
        // A temp row whose create already landed (list refresh raced the answer) is not shown twice.
        return orderEnvelopes(fresh.concat(temps.filter((temp) => !fresh.some((item) => item.clientKey === temp.clientKey))));
      });
      const wasOffline = Boolean(offlineRef.current);
      if (wasOffline) loadedFor.current = ''; // back online: refresh the open envelope from the Ledger
      setOffline(null);
      setListState({ status: 'ready', error: null });
      // M004-F0793: re-get ONLY the open envelope, and only when the list says it changed (or carries no updated_at); never every envelope.
      if (refreshOpen && !wasOffline) {
        const open = envelopesRef.current.find((item) => rowKey(item) === selectedRef.current);
        const record = open && !open.pending ? records.find((item) => item.envelope_id === open.envelope_id) : null;
        const entry = record ? detailsRef.current[record.envelope_id] : null;
        if (record && entry && entry.status !== 'loading' && (refreshOpen === 'force' || detailIsStale(record, loadedStamp.current[record.envelope_id]))) {
          loadDetailRef.current(forOrg, record.envelope_id);
        }
      }
      return records;
    } catch (cause) {
      if (seq !== listSeq.current || orgRef.current !== forOrg) return null;
      if (isUnreachable(cause) && applyOffline(forOrg)) {
        say('Ledger is not reachable. Showing your saved copy.');
        return null;
      }
      const failure = extractEnvelopeError(cause);
      setListState({ status: 'error', error: failure });
    }
    return null;
  }, [applyOffline, say]);
  loadListRef.current = loadList;

  // Keyed on orgId and a change of member, never on loadList's identity: that also follows userKey, which changes when the auth user
  // loads late; depending on it re-ran this reset mid-create, wiping the pending row and its selection (#235).
  useEffect(() => {
    setEnvelopes([]);
    setDetails({});
    setSelectedKey(null);
    setOffline(null);
    setFilter(NO_FILTER);
    setCreateError(null);
    setOpenReceiptId(null);
    setDetailsOpen(false);
    if (orgId) {
      setListState({ status: 'loading', error: null });
      loadListRef.current(orgId);
    }
  }, [orgId, memberEpoch]);

  // Ledger down: retry on window focus and every 30 s.
  useEffect(() => {
    if (!offline || !orgId) return undefined;
    const retry = () => loadList(orgId, { silent: true });
    const timer = setInterval(retry, OFFLINE_RETRY_MS);
    window.addEventListener('focus', retry);
    return () => { clearInterval(timer); window.removeEventListener('focus', retry); };
  }, [offline, orgId, loadList]);

  // ---- selection ------------------------------------------------------------------------------------------------------------------
  const listReady = listState.status === 'ready';
  useEffect(() => {
    if (!listReady) return;
    const match = envelopeId ? envelopesRef.current.find((item) => item.envelope_id === envelopeId) : null;
    if (match) { setSelectedKey(rowKey(match)); return; }
    if (!selectedRef.current) {
      const first = envelopesRef.current.find((item) => item.status !== 'deleted') || envelopesRef.current[0];
      if (first) setSelectedKey(rowKey(first));
    }
  }, [listReady, envelopeId]);

  // The selected row can disappear (a rolled-back create, a refreshed list): fall back to the first row.
  useEffect(() => {
    if (!listReady || !selectedKey) return;
    if (!envelopes.some((item) => rowKey(item) === selectedKey)) {
      const first = envelopes.find((item) => item.status !== 'deleted') || envelopes[0];
      setSelectedKey(first ? rowKey(first) : null);
    }
  }, [listReady, envelopes, selectedKey]);

  const selected = useMemo(() => envelopes.find((item) => rowKey(item) === selectedKey) || null, [envelopes, selectedKey]);
  const selectedId = selected && !selected.pending ? selected.envelope_id : null;

  const loadDetail = useCallback(async (forOrg, id) => {
    detailSeq.current[id] = (detailSeq.current[id] || 0) + 1;
    const seq = detailSeq.current[id];
    const epoch = mutationEpoch.current;
    setDetails((current) => ({
      ...current,
      [id]: current[id]?.status === 'ready' ? { ...current[id], error: null } : { status: 'loading', receipts: current[id]?.receipts || [], error: null },
    }));
    try {
      const data = unwrap(await envelopeApi.get(forOrg, id));
      if (orgRef.current !== forOrg || seq !== detailSeq.current[id]) return null;
      if (epoch !== mutationEpoch.current) return loadDetail(forOrg, id); // started before a local mutation completed: stale, ask again
      if (!data.envelope) {
        setDetails((current) => ({ ...current, [id]: { status: 'error', receipts: [], error: { code: '', message: NO_ENVELOPE_RETURNED_TEXT } } }));
        return null;
      }
      loadedStamp.current[id] = data.envelope.updated_at || envelopesRef.current.find((item) => item.envelope_id === id)?.updated_at || '';
      setEnvelopes((current) => mergeRecord(current, data.envelope));
      const receipts = sortReceipts(data.receipts || []);
      setDetails((current) => ({ ...current, [id]: { status: 'ready', receipts, error: null } }));
      return receipts;
    } catch (cause) {
      if (orgRef.current !== forOrg || seq !== detailSeq.current[id]) return null;
      if (isEnvelopeNotFound(cause)) {
        setDetails((current) => ({ ...current, [id]: { status: 'notfound', receipts: [], error: null } }));
      } else if (isUnreachable(cause) && detailsRef.current[id]?.receipts?.length) {
        setDetails((current) => ({ ...current, [id]: { ...current[id], status: 'ready' } }));
        loadList(forOrg, { silent: true });
      } else {
        setDetails((current) => ({ ...current, [id]: { status: 'error', receipts: current[id]?.receipts || [], error: extractEnvelopeError(cause) } }));
        if (isUnreachable(cause)) loadList(forOrg, { silent: true });
      }
    }
    return null;
  }, [loadList]);

  useEffect(() => {
    if (!orgId || !selectedId || !listReady) return;
    const key = `${orgId}/${selectedId}`;
    if (offline && detailsRef.current[selectedId]?.status === 'ready') return;
    if (loadedFor.current === key) return;
    loadedFor.current = key;
    loadDetail(orgId, selectedId);
  }, [orgId, selectedId, listReady, offline, loadDetail]);

  loadDetailRef.current = loadDetail;

  // ---- online refresh (M004-F0793; interim, replaced by the Ledger changes feed / CSN client from Spec core-ledger-bookkeeping 1.7.0) ----
  // Never while a move, upload or drawer edit is in flight: the refresh is remembered and runs when that work ends (or on the next tick).
  const trackBusy = useCallback((key, on) => {
    if (on) busyKeys.current.add(key); else busyKeys.current.delete(key);
    if (!on && pendingRefresh.current && runRefreshRef.current) runRefreshRef.current({ force: pendingRefresh.current === 'force' });
  }, []);
  const runRefresh = useCallback(({ force = false } = {}) => {
    if (!orgRef.current || offlineRef.current) return; // offline keeps its own retry
    if (movesInFlight.current > 0 || busyKeys.current.size > 0) {
      if (force || !pendingRefresh.current) pendingRefresh.current = force ? 'force' : 'soft';
      return;
    }
    pendingRefresh.current = '';
    loadList(orgRef.current, { silent: true, refreshOpen: force ? 'force' : true });
  }, [loadList]);
  runRefreshRef.current = runRefresh;
  useEffect(() => {
    if (!orgId || offline || !listReady) return undefined;
    const isVisible = () => document.visibilityState === 'visible';
    const onFocus = () => runRefreshRef.current();
    const onVisibility = () => { if (isVisible()) runRefreshRef.current(); };
    const timer = setInterval(() => { if (isVisible()) runRefreshRef.current(); }, ONLINE_REFRESH_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisibility); };
  }, [orgId, offline, listReady]);

  const retryDetail = () => { loadedFor.current = `${orgId}/${selectedId}`; loadDetail(orgId, selectedId); };

  const select = (record) => {
    setSelectedKey(rowKey(record));
    setFilter(NO_FILTER);
    if (!record.pending && typeof onOpenEnvelope === 'function') onOpenEnvelope(record.envelope_id);
  };

  // ---- the saved copy -------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    if (!orgId || offline || listState.status !== 'ready') return;
    const real = envelopes.filter((item) => !item.pending);
    if (!real.length) return;
    const receipts = {};
    Object.entries(details).forEach(([id, entry]) => { if (entry.status === 'ready') receipts[id] = entry.receipts; });
    writeCache(userKey, orgId, { envelopes: real, receipts });
  }, [envelopes, details, offline, orgId, userKey, listState.status]);

  // ---- keyboard: N focuses New envelope -------------------------------------------------------------------------------------------
  useEffect(() => {
    const onKey = (event) => {
      if ((event.key !== 'n' && event.key !== 'N') || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (!inputRef.current || inputRef.current.disabled) return;
      event.preventDefault();
      inputRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Layout effect: focus lands in the same commit as the restored text (no frame where the input shows text but is not focused).
  useLayoutEffect(() => { if (focusTick && inputRef.current && !inputRef.current.disabled) inputRef.current.focus(); }, [focusTick]);

  // The empty card promises the input is ready: focus it.
  const noEnvelopes = listReady && !envelopes.length;
  useEffect(() => { if (noEnvelopes && !offline) setFocusTick((tick) => tick + 1); }, [noEnvelopes, offline]);

  // ---- create (section 4) ---------------------------------------------------------------------------------------------------------
  const createEnvelope = async (name, clientKey, key, previousKey) => {
    const forOrg = orgRef.current;
    try {
      // Core 202 (pending): the client replays the SAME key itself (envelopeApi keyedSend); the row stays pending meanwhile.
      const options = { onPending: () => say(`Still processing ${name}. The Ledger has not answered yet.`) };
      let data;
      try {
        data = unwrap(await envelopeApi.create(forOrg, { name }, key, options));
      } catch (cause) {
        if (cause?.response) throw cause;
        await delay(CREATE_RETRY_DELAY_MS); // a network failure: ONE retry, same Idempotency-Key
        data = unwrap(await envelopeApi.create(forOrg, { name }, key, options));
      }
      if (!data.envelope_id) throw Object.assign(new Error('The Ledger sent no envelope.'), { response: { status: 502, data: { error: 'ENVELOPE_NO_RECORD', message: 'The Ledger sent no envelope.' } } });
      failedKey.current = null;
      mutationEpoch.current += 1;
      setEnvelopes((current) => reconcileCreate(current, clientKey, data));
      setDetails((current) => ({ ...current, [data.envelope_id]: { status: 'ready', receipts: [], error: null } }));
      loadedFor.current = `${forOrg}/${data.envelope_id}`;
      say(`Created envelope ${name}`);
      if (selectedRef.current === clientKey && typeof onOpenEnvelope === 'function') onOpenEnvelope(data.envelope_id);
    } catch (cause) {
      const failure = extractEnvelopeError(cause);
      // A network failure, or a still-pending create, keeps the key: a later retry of the same name replays it, never a second create.
      failedKey.current = !cause?.response || failure.code === STILL_PROCESSING_CODE ? { name, key } : null;
      mutationEpoch.current += 1;
      setEnvelopes((current) => rollbackCreate(current, clientKey));
      if (selectedRef.current === clientKey) setSelectedKey(previousKey);
      if (!textRef.current) setText(name);
      setCreateError({ ...failure, name });
      setFocusTick((tick) => tick + 1);
      say(failure.code === STILL_PROCESSING_CODE ? `${name}: ${failure.message}` : `Could not create ${name}. ${failure.message}${failure.code ? ` (${failure.code})` : ''}`);
      if (isUnreachable(cause)) loadList(forOrg, { silent: true });
    } finally {
      inflight.current.delete(name.toLowerCase());
    }
  };

  const submitCreate = () => {
    const name = text.trim();
    if (!name || offline || !orgRef.current) return;
    if (inflight.current.has(name.toLowerCase())) return; // a repeated Enter while the same name is in flight
    inflight.current.add(name.toLowerCase());
    const previous = failedKey.current;
    const key = previous && previous.name === name ? previous.key : newIdempotencyKey();
    failedKey.current = null;
    const clientKey = newClientKey();
    const previousKey = selectedRef.current;
    setCreateError(null);
    setText('');
    setEnvelopes((current) => addTemp(current, makeTempEnvelope(name, clientKey)));
    setSelectedKey(clientKey);
    setFilter(NO_FILTER);
    say(`Creating envelope ${name}`);
    createEnvelope(name, clientKey, key, previousKey);
  };

  // ---- envelope actions -----------------------------------------------------------------------------------------------------------
  const applyRecord = useCallback((record) => {
    mutationEpoch.current += 1; // a local write (edit, close, delete, restore, claim) completed
    if (!record?.envelope_id) return;
    setEnvelopes((current) => orderEnvelopes(mergeRecord(current, record)));
  }, []);

  const restore = async (record) => {
    try {
      applyRecord(unwrap(await envelopeApi.restore(orgId, record.envelope_id)));
    } catch (cause) {
      setToast(extractEnvelopeError(cause).message);
    }
  };

  const togglePurge = async () => {
    const open = !purge.open;
    setPurge((current) => ({ ...current, open, error: '' }));
    if (!open) return;
    try {
      setPurge({ open: true, rows: unwrap(await envelopeApi.purgeReceipts(orgId)).purge_receipts || [], error: '' });
    } catch (cause) {
      setPurge({ open: true, rows: [], error: extractEnvelopeError(cause).message });
    }
  };

  // ---- receipts -------------------------------------------------------------------------------------------------------------------
  const receiptsOf = (id) => details[id]?.receipts || [];
  const onReceiptSaved = (saved) => {
    setDetails((current) => {
      const entry = current[selectedId];
      if (!entry) return current;
      return { ...current, [selectedId]: { ...entry, receipts: entry.receipts.map((item) => (item.receipt_document_id === saved.receipt_document_id ? { ...item, ...saved } : item)) } };
    });
  };
  const onReceiptGone = (docId, how) => {
    mutationEpoch.current += 1;
    setDetails((current) => {
      const entry = current[selectedId];
      if (!entry) return current;
      return { ...current, [selectedId]: { ...entry, receipts: entry.receipts.filter((item) => item.receipt_document_id !== docId) } };
    });
    setOpenReceiptId(null);
    setToast(how === 'promoted' ? 'Receipt moved to the org receipt queue.' : 'Receipt removed from this envelope.');
    say(how === 'promoted' ? 'Receipt moved to the org receipt queue.' : 'Receipt removed from this envelope.');
    loadList(orgId, { silent: true });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(''), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  // ---- derived view ---------------------------------------------------------------------------------------------------------------
  const selectedDetails = useMemo(() => {
    if (selectedId) return details[selectedId] || { status: 'loading', receipts: [], error: null };
    return selected?.pending ? { status: 'ready', receipts: [], error: null } : null;
  }, [selectedId, selected, details]);
  const loadedReceipts = selectedDetails && (selectedDetails.status === 'ready' || selectedDetails.receipts.length) ? selectedDetails.receipts : undefined;
  const totals = selected && !selected.pending ? totalsFor(selected, loadedReceipts) : null;
  const visible = useMemo(() => filterReceipts(selectedDetails?.receipts || [], filter), [selectedDetails, filter]);

  const lockedReason = selected?.locked ? `Submitted — returned envelopes unlock.` : '';
  const readOnlyReason = offline ? OFFLINE_REASON : '';
  const writeBlock = readOnlyReason || (selected?.locked ? lockedReason : '');
  const openReceipt = openReceiptId ? receiptsOf(selectedId).find((item) => item.receipt_document_id === openReceiptId) : null;
  const defaultCurrency = defaultCurrencyFor(receiptsOf(selectedId));

  // M004-F0746: read the open envelope again only while a receipt is queued or reading (2 s, backing off to 10 s); announce each change.
  const readingNow = needsPolling(selectedDetails?.receipts);
  useOcrPolling({
    active: Boolean(selectedId && orgId && !offline && selectedDetails?.status === 'ready' && readingNow),
    signature: `${selectedId}/${pollSignature(selectedDetails?.receipts)}`,
    reload: () => loadDetail(orgId, selectedId),
  });
  const ocrSeen = useRef({});
  useEffect(() => {
    const list = selectedDetails?.receipts || [];
    const previous = ocrSeen.current;
    const next = {};
    list.forEach((item) => {
      const id = item.receipt_document_id;
      const status = item.ocr?.status || '';
      next[id] = status;
      if (previous[id] && previous[id] !== status) {
        const name = receiptHeadline(item).text;
        if (status === 'suggested') setOcrAnnouncement(`${name}: read, ready to check.`);
        else if (status === 'failed') setOcrAnnouncement(`${name}: could not be read. Enter the details yourself.`);
        else if (status === 'unavailable') setOcrAnnouncement(`${name}: automatic reading is off. Enter the details yourself.`);
        else if (status === 'confirmed') setOcrAnnouncement(`${name}: confirmed.`);
      }
    });
    ocrSeen.current = next;
  }, [selectedDetails]);
  // ---- submit a claim (M004-F0747, design sections 3.3 / 8 / 8.0; claimant side only) -------------------------------------------
  const removeEnvelope = useCallback((id) => {
    mutationEpoch.current += 1;
    setEnvelopes((current) => current.filter((item) => item.envelope_id !== id));
    setDetails((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== id)));
  }, []);
  const { claimUi } = useClaimFlow({
    orgId,
    organisations,
    envelopes,
    selected,
    selectedId,
    ready: selectedDetails?.status === 'ready',
    receipts: receiptsOf(selectedId),
    offline: Boolean(offline),
    applyRecord,
    removeEnvelope,
    reload: () => loadList(orgId, { silent: true }),
    say,
    setToast,
    showIds: (ids) => setFilter({ query: '', status: 'all', ids }),
    onAttentionCount,
  });

  // ---- move receipts (M004-F0745, design section 6) -------------------------------------------------------------------------------
  const [selectionState, setSelectionState] = useState(NO_SELECTION);
  const [activeId, setActiveId] = useState(null);
  const [menu, setMenu] = useState(null);
  const [moveToast, setMoveToast] = useState(null);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [refusedRow, setRefusedRow] = useState(null);
  const [restored, setRestored] = useState([]);
  const [movingIds, setMovingIds] = useState([]); // #264: receipts whose move the Ledger has not finally answered (202 pending)
  const [focusRequest, setFocusRequest] = useState({ id: null, tick: 0 });
  const [narrow, setNarrow] = useState(isNarrowViewport);
  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const overRef = useRef(null);
  const blockedRef = useRef(null); // the blocked row the pointer was last over; the browser's own dragleave on release must not forget it
  const menuRef = useRef(null);
  const moveToastRef = useRef(null);
  const pendingMove = useRef(Promise.resolve(null)); // resolves to the ids the last move really moved, or false when it was refused
  const toastSeq = useRef(0);
  const moveSeq = useRef(0);
  const doMoveRef = useRef(null);
  const undoRef = useRef(null);
  const visibleIdsRef = useRef([]);
  const activeRef = useRef(null);
  menuRef.current = menu;

  const visibleIds = useMemo(() => visible.map((item) => item.receipt_document_id), [visible]);
  visibleIdsRef.current = visibleIds;
  const selection = useMemo(() => new Set(selectionState.ids), [selectionState.ids]);
  const activeEffective = visibleIds.includes(activeId) ? activeId : visibleIds[0] || null;
  activeRef.current = activeEffective;
  const isOffline = Boolean(offline);
  const giveBlock = selected ? giveReason(selected) : 'Choose an envelope first.';

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(max-width: 719px)');
    const onChange = () => setNarrow(query.matches);
    onChange();
    if (query.addEventListener) query.addEventListener('change', onChange); else query.addListener(onChange);
    return () => { if (query.removeEventListener) query.removeEventListener('change', onChange); else query.removeListener(onChange); };
  }, []);

  // Another envelope (or a filter that hides a tile) ends the selection of what is no longer there.
  useEffect(() => { setSelectionState(NO_SELECTION); setActiveId(null); setMenu(null); }, [selectedKey]);
  useEffect(() => {
    setSelectionState((current) => {
      const keep = current.ids.filter((id) => visibleIds.includes(id));
      return keep.length === current.ids.length ? current : { ...current, ids: keep };
    });
  }, [visibleIds]);

  const focusTile = useCallback((id) => {
    const node = id && rootRef.current ? rootRef.current.querySelector(`[data-receipt-id="${id}"]`) : null;
    if (node) node.focus();
  }, []);
  const clearSelection = useCallback(() => setSelectionState(NO_SELECTION), []);

  const toggleId = (id) => {
    if (giveBlock) { say(giveBlock); return; }
    setSelectionState((current) => ({ ids: current.ids.includes(id) ? current.ids.filter((item) => item !== id) : current.ids.concat(id), anchor: id }));
  };
  const extendTo = (id) => {
    setSelectionState((current) => ({ ids: rangeIds(visibleIdsRef.current, current.anchor || activeRef.current || id, id), anchor: current.anchor || activeRef.current || id }));
  };
  const selectAll = () => {
    if (giveBlock) { say(giveBlock); return; }
    setSelectionState({ ids: visibleIdsRef.current.slice(), anchor: activeRef.current });
  };
  const onFocusTile = (id, extend) => {
    if (extend) { if (giveBlock) say(giveBlock); else extendTo(id); }
    setActiveId(id);
    focusTile(id);
  };
  const openReceiptDrawer = (receipt, element) => { openerRef.current = element; setOpenReceiptId(receipt.receipt_document_id); };
  const onClickTile = (event, receipt, element) => {
    const id = receipt.receipt_document_id;
    setActiveId(id);
    if (event.target.closest?.('.ew-tile-check') || event.ctrlKey || event.metaKey) { toggleId(id); return; }
    if (event.shiftKey) { if (giveBlock) say(giveBlock); else extendTo(id); return; }
    if (event.detail > 0 && selection.size > 0) { toggleId(id); return; }
    openReceiptDrawer(receipt, element);
  };
  const onLongPress = (id) => {
    if (giveBlock) { say(giveBlock); return; }
    setActiveId(id);
    setSelectionState((current) => (current.ids.includes(id) ? current : { ids: current.ids.concat(id), anchor: id }));
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(12);
  };

  // ---- the Move to… menu ----
  const openMenu = (origin, tileId) => {
    if (!selected || selected.pending) return;
    if (isOffline) { say(OFFLINE_HINT); return; }
    if (giveBlock) { say(giveBlock); return; }
    let ids = visibleIds.filter((id) => selection.has(id));
    if (!ids.length) {
      const target = tileId || activeRef.current;
      if (!target) return;
      ids = [target];
      setSelectionState({ ids, anchor: target });
    }
    setMenu({ origin, ids });
  };
  const closeMenu = (restoreFocus = true) => {
    const origin = menuRef.current?.origin;
    setMenu(null);
    if (!restoreFocus) return;
    setTimeout(() => {
      const node = origin === 'button' ? rootRef.current?.querySelector('[data-testid="envelope-move-to"]') : rootRef.current?.querySelector(`[data-receipt-id="${activeRef.current}"]`);
      if (node) node.focus();
    }, 0);
  };

  // ---- toast ----
  const showToast = (next) => {
    toastSeq.current += 1;
    const toast = { id: toastSeq.current, ...next };
    moveToastRef.current = toast;
    setMoveToast(toast);
    return toast.id;
  };
  const dismissToast = useCallback((id) => {
    if (!moveToastRef.current || (id !== undefined && moveToastRef.current.id !== id)) return;
    moveToastRef.current = null;
    setMoveToast(null);
  }, []);

  // ---- the move itself ----
  const flashRestored = (ids) => {
    setRestored(ids);
    setTimeout(() => setRestored([]), RESTORE_FLASH_MS);
  };

  // M004-F0794 b: after a 404 ENVELOPE_RECEIPT_NOT_FOUND, re-read the list and both envelopes, then look for the receipt. Returns the
  // sentence to show, or '' (still where we thought, or the re-read failed): the Ledger's own text then stays.
  const resolveStaleMove = async (forOrg, fromId, toId, docIds, currentId) => {
    const docId = docIds[0];
    const holds = (list, any = [docId]) => Array.isArray(list) && list.some((item) => any.includes(item.receipt_document_id));
    const records = await loadList(forOrg, { silent: true });
    const fromReceipts = await loadDetailRef.current(forOrg, fromId);
    const toReceipts = await loadDetailRef.current(forOrg, toId);
    if (!records || !fromReceipts || !toReceipts) return '';
    const label = (id) => { const record = records.find((item) => item.envelope_id === id); return record ? envelopeLabel(record) : ''; };
    // 409 ENVELOPE_RECEIPT_MOVED names the destination itself (null: it is gone). Both envelopes were re-read above.
    if (currentId === null) return staleMoveSentence({ found: false });
    if (typeof currentId === 'string') return staleMoveSentence({ found: true, elsewhereName: label(currentId) });
    if (holds(fromReceipts, docIds)) return ''; // some of them are still here: the refusal is about another receipt, keep the Ledger's text
    if (holds(toReceipts)) return staleMoveSentence({ found: true, elsewhereName: label(toId) });
    const others = records.filter((item) => !item.pending && item.status !== 'deleted' && item.envelope_id !== fromId && item.envelope_id !== toId && item.counts?.receipts > 0);
    for (const record of others) {
      try {
        const data = unwrap(await envelopeApi.get(forOrg, record.envelope_id));
        if (holds(data.receipts)) return staleMoveSentence({ found: true, elsewhereName: envelopeLabel(record) });
      } catch (cause) { return ''; } // cannot tell: keep the Ledger's own text
    }
    return staleMoveSentence({ found: false });
  };

  // #264: a pending move whose outcome is unknown (retries exhausted, timeout, 5xx), or a stale-looking refusal AFTER a retry: read the
  // list and both envelopes and say what is true. Returns 'moved' (all ids in the target), 'notmoved' (all still in the source),
  // 'partial', or null (a read failed: cannot tell).
  const readBackMove = async (forOrg, fromId, toId, docIds) => {
    const records = await loadList(forOrg, { silent: true });
    const fromReceipts = await loadDetailRef.current(forOrg, fromId);
    const toReceipts = await loadDetailRef.current(forOrg, toId);
    if (!records || !fromReceipts || !toReceipts) return null;
    const inTo = docIds.filter((id) => toReceipts.some((item) => item.receipt_document_id === id)).length;
    const inFrom = docIds.filter((id) => fromReceipts.some((item) => item.receipt_document_id === id)).length;
    if (inTo === docIds.length) return 'moved';
    if (inFrom === docIds.length) return 'notmoved';
    return 'partial';
  };

  // `after`: a move still in flight that this one follows (Undo pressed before the Ledger has answered): the screen reverts at once, the request waits.
  const doMove = async ({ fromId, toId, ids, known = [], kind = 'move', after = null }) => {
    const forOrg = orgRef.current;
    moveSeq.current += 1;
    const moveId = moveSeq.current;
    const records = envelopesRef.current;
    const fromRecord = records.find((item) => item.envelope_id === fromId);
    const toRecord = records.find((item) => item.envelope_id === toId);
    const why = giveReason(fromRecord, { offline: Boolean(offlineRef.current) }) || takeReason(toRecord, { offline: Boolean(offlineRef.current) });
    if (why) { say(why); return; }
    if (ids.length > MOVE_LIMIT) { showToast({ kind: 'error', text: LIMIT_TEXT }); say(LIMIT_TEXT); return; }
    const toName = envelopeLabel(toRecord);
    const snapshot = { details: { [fromId]: detailsRef.current[fromId], [toId]: detailsRef.current[toId] }, from: fromRecord, to: toRecord };
    const viewing = selectedRef.current ? envelopesRef.current.find((item) => rowKey(item) === selectedRef.current)?.envelope_id : null;
    const next = nextFocusId(visibleIdsRef.current, ids);
    const movedReceipts = applyMoveToDetails(detailsRef.current, fromId, toId, ids, known).moved;
    setDetails((current) => applyMoveToDetails(current, fromId, toId, ids, known).details);
    setEnvelopes((current) => applyMoveToRecords(current, fromId, toId, movedReceipts));
    setSelectionState((current) => ({ ...current, ids: current.ids.filter((id) => !ids.includes(id)) })); // what moved is no longer selected
    setMenu(null);
    if (viewing === fromId) setFocusRequest((current) => ({ id: next, tick: current.tick + 1 }));
    else if (viewing === toId) setFocusRequest((current) => ({ id: ids[0], tick: current.tick + 1 }));
    const sentence = kind === 'undo' ? undoneSentence(ids.length, toName) : moveSentence(ids.length, toName);
    const idempotencyKey = newIdempotencyKey(); // ONE key per user intent: every 202 replay and the final answer carry it
    let replayed = false;
    let toastId = showToast({ kind: 'moved', text: sentence, undo: kind === 'undo' ? null : { fromId, toId, ids, receipts: movedReceipts } });
    say(kind === 'undo' ? sentence : `${sentence} Press Z to undo.`);

    mutationEpoch.current += 1; // the optimistic picture is the newest: an older read must not overwrite it
    movesInFlight.current += 1;
    const run = (async () => {
      try {
        let send = ids;
        if (after) {
          const prior = await after;
          if (prior === false) return false; // the move this one would undo was refused and rolled back: there is nothing to send
          if (Array.isArray(prior)) send = ids.filter((id) => prior.includes(id)); // only what that move really moved can be moved back
          if (!send.length) return [];
        }
        const onPending = () => {
          if (orgRef.current !== forOrg) return;
          replayed = true;
          setMovingIds(send);
          // Never "Moved" on a 202: the toast says the move is pending until the Ledger's final answer.
          const text = `Moving ${ids.length === 1 ? '1 receipt' : `${ids.length} receipts`} to ${toName}\u2026 the Ledger has not confirmed yet.`;
          toastId = showToast({ kind: 'pending', text, undo: null });
          say(text);
        };
        const data = unwrap(await envelopeApi.receiptMove(forOrg, fromId, toId, send, idempotencyKey, { onPending }));
        mutationEpoch.current += 1; // completed
        if (orgRef.current !== forOrg) return false;
        // A newer move (the Undo of this one) owns the numbers now: this answer must not flash the old state back.
        if (moveSeq.current === moveId) setEnvelopes((current) => mergeMoveSide(mergeMoveSide(current, data.from), data.to));
        const movedIds = Array.isArray(data.moved) ? data.moved : ids;
        const there = Array.isArray(data.already_there) ? data.already_there : [];
        if (there.length || replayed) { // our picture was stale for these (or a pending period let another read run): read the truth for both lists
          [fromId, toId].forEach((id) => { if (snapshot.details[id]?.status === 'ready') loadDetail(forOrg, id); });
        }
        if (replayed && moveToastRef.current?.id === toastId) { // the pending toast gives way to the confirmed one, only now
          const confirmed = movedIds.length
            ? { kind: 'moved', text: sentence, undo: kind === 'undo' ? null : { fromId, toId, ids: movedIds, receipts: movedReceipts.filter((item) => movedIds.includes(item.receipt_document_id)) } }
            : { kind: 'moved', undo: null, text: alreadyThereSentence(there.length, toName) };
          toastId = showToast(confirmed);
          say(confirmed.text);
        } else if (kind === 'move' && moveToastRef.current?.id === toastId) {
          const updated = movedIds.length
            ? { ...moveToastRef.current, undo: { fromId, toId, ids: movedIds, receipts: movedReceipts.filter((item) => movedIds.includes(item.receipt_document_id)) } }
            : { ...moveToastRef.current, undo: null, text: alreadyThereSentence(there.length, toName) };
          moveToastRef.current = updated;
          setMoveToast(updated);
        }
        return movedIds;
      } catch (cause) {
        mutationEpoch.current += 1; // completed (refused)
        if (orgRef.current !== forOrg) return false;
        const failure = extractEnvelopeError(cause);
        const notFound = failure.status === 404 && failure.code === STALE_MOVE_CODE;
        const movedBody = failure.status === 409 && failure.code === MOVED_MOVE_CODE ? (cause?.response?.data?.details ?? cause?.response?.data?.data ?? {}) : null;
        const stale = notFound || Boolean(movedBody);
        const rollback = () => {
          setDetails((current) => {
            const out = { ...current };
            [fromId, toId].forEach((id) => { if (snapshot.details[id]) out[id] = snapshot.details[id]; });
            return out;
          });
          setEnvelopes((current) => current.map((item) => {
            if (item.envelope_id === fromId && snapshot.from) return { ...item, counts: snapshot.from.counts, totals: snapshot.from.totals };
            if (item.envelope_id === toId && snapshot.to) return { ...item, counts: snapshot.to.counts, totals: snapshot.to.totals };
            return item;
          }));
        };
        // #264: a retry (after a 202) answered "moved" or "not found" may be OUR move having landed; the readback decides. A timeout / 5xx /
        // exhausted-pending outcome is unknown: read back too, never a definite failure.
        const unknown = failure.code === STILL_PROCESSING_CODE || classifyRelayOutcome(cause) === 'unknown';
        if ((replayed && stale) || unknown) {
          const truth = await readBackMove(forOrg, fromId, toId, ids);
          if (orgRef.current !== forOrg) return false;
          if (truth === 'moved') {
            const text = moveSentence(ids.length, toName);
            showToast({ kind: 'moved', text, undo: kind === 'undo' ? null : { fromId, toId, ids, receipts: movedReceipts } });
            say(text);
            return ids;
          }
          if (unknown) {
            if (truth === null) rollback(); // cannot tell: the optimistic picture goes back, the Ledger was not heard
            flashRestored(ids);
            const text = truth === 'notmoved'
              ? `The Ledger did not confirm the move, and the re-read shows nothing was moved. ${ids.length === 1 ? 'The receipt is' : 'The receipts are'} still in the original envelope.`
              : truth === 'partial'
                ? 'The Ledger did not confirm the move. Some of the receipts moved; the lists now show what is true.'
                : 'The Ledger did not confirm the move and the lists could not be re-read. Refresh to see what is true.';
            showToast({ kind: 'error', text, code: failure.code || '' });
            say(text);
            return false;
          }
        }
        const currentId = movedBody && 'current_envelope_id' in movedBody ? movedBody.current_envelope_id : undefined; // undefined: the Ledger did not say
        rollback();
        flashRestored(ids);
        if (viewing === fromId) setFocusRequest((current) => ({ id: ids[0], tick: current.tick + 1 }));
        // M004-F0794 b: the receipt is not where we thought. Re-read both envelopes and say what happened to it.
        const staleText = stale ? await resolveStaleMove(forOrg, fromId, toId, ids, currentId) : '';
        if (orgRef.current !== forOrg) return false;
        if (staleText) {
          showToast({ kind: 'error', text: staleText, code: '' });
          say(staleText);
          return false;
        }
        showToast({ kind: 'error', text: failure.message, code: failure.code || '' });
        say(`Could not move ${ids.length === 1 ? 'the receipt' : `${ids.length} receipts`}. ${failure.message}${failure.code ? ` (${failure.code})` : ''}`);
        if (!stale) {
          loadList(forOrg, { silent: true }); // re-read from the server: the lists show what is true now
          [fromId, toId].forEach((id) => { if (snapshot.details[id]?.status === 'ready') loadDetail(forOrg, id); });
        }
        return false;
      }
    })();
    pendingMove.current = run;
    try { await run; } finally {
      setMovingIds([]);
      movesInFlight.current -= 1;
      if (pendingRefresh.current && runRefreshRef.current) runRefreshRef.current({ force: pendingRefresh.current === 'force' });
    }
  };
  doMoveRef.current = doMove;

  const undoMove = async () => {
    const current = moveToastRef.current;
    if (!current?.undo) return;
    const { fromId, toId, ids, receipts } = current.undo;
    // The inverse follows whatever is still in flight; if that move is refused the Ledger never moved anything and nothing is sent.
    await doMoveRef.current({ fromId: toId, toId: fromId, ids, known: receipts, kind: 'undo', after: pendingMove.current });
  };
  undoRef.current = undoMove;

  // Z (or Ctrl/Cmd+Z) while the toast is up undoes the move.
  useEffect(() => {
    const onKey = (event) => {
      if ((event.key !== 'z' && event.key !== 'Z') || event.altKey || event.shiftKey) return;
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (!moveToastRef.current?.undo) return;
      event.preventDefault();
      undoRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const pickTarget = (target) => {
    const current = menuRef.current;
    setMenu(null);
    if (!current || !selectedId) return;
    doMove({ fromId: selectedId, toId: target.id, ids: current.ids });
  };

  // ---- drag and drop (mouse) ----
  const finishDrag = () => {
    removeGhost();
    dragRef.current = null;
    overRef.current = null;
    blockedRef.current = null;
    setDrag(null);
    setOver(null);
  };
  const flashRefusal = (id, reason) => {
    say(reason);
    setRefusedRow({ id, reason });
    setTimeout(() => setRefusedRow((current) => (current && current.id === id ? null : current)), REFUSAL_FLASH_MS);
  };
  const onTileDragStart = (event, receipt) => {
    const id = receipt.receipt_document_id;
    if (giveBlock || isOffline || !selectedId) { event.preventDefault(); return; }
    const ids = selection.has(id) ? visibleIds.filter((item) => selection.has(item)) : [id];
    const picture = event.currentTarget.querySelector('img');
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ ids, from: selectedId }));
    event.dataTransfer.setData('text/plain', ids.length === 1 ? 'receipt' : `${ids.length} receipts`);
    event.dataTransfer.setDragImage(buildGhost({ count: ids.length, title: receiptHeadline(receipt).text, src: picture ? picture.src : '' }), 28, 28);
    dragRef.current = { ids, fromId: selectedId };
    // The tiles dim one tick later: changing the dragged element inside dragstart makes some browsers cancel the drag.
    setTimeout(() => { if (dragRef.current) setDrag(dragRef.current); }, 0);
  };
  const onTileDragEnd = () => {
    const hovered = blockedRef.current;
    finishDrag();
    if (hovered) flashRefusal(hovered.id, hovered.reason); // released over a row that cannot take them (the browser sends no drop for it)
  };
  const dropInfo = (record, active) => {
    if (record.envelope_id === active.fromId) return { state: 'same', reason: '' };
    const reason = takeReason(record, { offline: isOffline });
    return reason ? { state: 'blocked', reason } : { state: 'ok', reason: '' };
  };
  const dropFor = (record) => {
    const hovered = over && over.id === record.envelope_id ? over : null;
    const refused = !hovered && refusedRow && refusedRow.id === record.envelope_id ? refusedRow : null;
    const info = drag && !hovered ? dropInfo(record, drag) : null;
    return {
      state: hovered ? hovered.state : refused ? 'blocked' : null,
      reason: hovered ? hovered.reason : refused ? refused.reason : '',
      blocked: Boolean(info && info.state !== 'ok'),
      dragging: Boolean(drag),
      onOver: (event) => {
        const active = dragRef.current;
        if (!active) return;
        event.preventDefault();
        const info2 = dropInfo(record, active);
        event.dataTransfer.dropEffect = info2.state === 'ok' ? 'move' : 'none';
        blockedRef.current = info2.state === 'blocked' ? { id: record.envelope_id, reason: info2.reason } : null;
        if (!overRef.current || overRef.current.id !== record.envelope_id || overRef.current.state !== info2.state) {
          overRef.current = { id: record.envelope_id, ...info2 };
          setOver(overRef.current);
        }
      },
      onLeave: (event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (event.relatedTarget) blockedRef.current = null; // moved on to something else; a null target is the browser ending the drag
        if (overRef.current && overRef.current.id === record.envelope_id) { overRef.current = null; setOver(null); }
      },
      onDrop: (event) => {
        const active = dragRef.current;
        if (!active) return;
        event.preventDefault();
        const info2 = dropInfo(record, active);
        finishDrag(); // the moved tile leaves the page at once, so the browser's dragend never reaches it: end the drag here
        if (info2.state === 'ok') doMove({ fromId: active.fromId, toId: record.envelope_id, ids: active.ids });
        else if (info2.state === 'blocked') flashRefusal(record.envelope_id, info2.reason);
      },
    };
  };

  const menuNode = menu ? (
    <ReceiptMoveMenu
      targets={moveTargets(envelopes, selectedId, { offline: isOffline })}
      count={menu.ids.length}
      asSheet={narrow}
      onPick={pickTarget}
      onClose={() => closeMenu(true)}
    />
  ) : null;
  const move = {
    selection,
    activeId: activeEffective,
    canGive: !giveBlock,
    offline: isOffline,
    dragging: new Set(drag ? drag.ids : []),
    restored: new Set(restored),
    moving: new Set(movingIds),
    focusRequest,
    menuOpen: Boolean(menu),
    menuSlot: narrow ? null : menuNode,
    onToggle: toggleId,
    onClear: clearSelection,
    onSelectAll: selectAll,
    onOpenMenu: openMenu,
    onFocusTile,
    onActive: setActiveId,
    onClickTile,
    onOpen: openReceiptDrawer,
    onLongPress,
    onDragStart: onTileDragStart,
    onDragEnd: onTileDragEnd,
  };

  if (organisationsLoading) {
    return <section className="ew-root" data-testid="envelopes-tab" aria-label="My Envelopes"><div className="ew-layout"><EnvelopeSidebar envelopes={[]} detailsById={{}} loading inputRef={inputRef} text="" onText={() => {}} onSubmit={() => {}} onClear={() => {}} readOnlyReason="Loading…" onSelect={() => {}} onRestore={() => {}} /></div></section>;
  }
  if (!organisations.length) {
    return (
      <section className="ew-root" data-testid="envelopes-tab" aria-label="My Envelopes">
        <p className="ew-empty-card" data-testid="envelopes-no-org">None of your organisations has a Ledger, so there is nowhere to keep envelopes yet.</p>
      </section>
    );
  }

  const purgePanel = (
    <div className="ew-purge">
      <button type="button" className="ew-link" data-testid="envelopes-purge-link" aria-expanded={purge.open} onClick={togglePurge}>Purge receipts</button>
      {purge.open ? (
        <div className="ew-purge-panel" data-testid="envelopes-purge-panel">
          {purge.error ? <p className="ew-error" role="alert">{purge.error}</p> : null}
          {!purge.error && !purge.rows.length ? <p className="ew-muted">Nothing has been purged yet.</p> : null}
          <ul>
            {purge.rows.map((row) => (
              <li key={row.purge_id} data-testid="envelopes-purge-row"><strong>{row.name}</strong> — purged {formatStamp(row.purged_at)}, {row.receipts_purged} receipts, {formatBytes(row.bytes_purged)} ({row.reason})</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );

  // WebUI #240: every receipt the workspace has read (all envelopes loaded so far): the duplicate marker and the upload dedupe use it.
  const workspaceReceipts = Object.values(details).flatMap((entry) => (entry.status === 'ready' ? entry.receipts : []));

  return (
    <section className="ew-root" data-testid="envelopes-tab" aria-label="My Envelopes" ref={rootRef}>
      <div className="ew-announcer ew-visually-hidden" aria-live="polite" role="status" data-testid="envelopes-announcer">{announcement}</div>
      <div className="ew-announcer ew-visually-hidden" aria-live="polite" role="status" data-testid="envelopes-ocr-announcer">{ocrAnnouncement}</div>
      <header className="ew-head">
        {noEnvelopes ? <span /> : <p className="ew-tagline">{ENVELOPES_TAB_COPY}</p>}
        {organisations.length > 1 ? (
          <label className="ew-org">
            Organisation
            <select data-testid="envelopes-org-selector" value={orgId} onChange={(event) => setOrgId(event.target.value)}>
              {organisations.map((org) => <option key={org.id} value={String(org.id)}>{org.name}</option>)}
            </select>
          </label>
        ) : null}
        {!offline && listState.status === 'ready' ? (
          <button type="button" className="ew-btn ew-refresh" data-testid="envelopes-refresh" aria-label="Refresh envelopes" onClick={() => runRefresh({ force: true })}>Refresh</button>
        ) : null}
      </header>

      {offline ? (
        <p className="ew-banner" role="status" data-testid="envelopes-offline-banner">
          Ledger is not reachable — showing your saved copy from {formatSavedAt(offline.savedAt)}
          {' '}
          <button type="button" className="ew-link" onClick={() => loadList(orgId, { silent: true })}>Try again</button>
        </p>
      ) : null}
      {toast ? <p className="ew-toast" role="status" data-testid="envelope-toast">{toast}</p> : null}

      {listState.status === 'error' ? (
        <div className="ew-error ew-list-error" role="alert" data-testid="envelopes-error">
          <p>{listState.error.message}</p>
          {listState.error.code ? <p>Error code: <code data-testid="envelopes-error-code">{listState.error.code}</code></p> : null}
          <button type="button" className="ew-btn" data-testid="envelopes-retry" onClick={() => { setListState({ status: 'loading', error: null }); loadList(orgId); }}>Try again</button>
        </div>
      ) : null}

      {listState.status !== 'error' ? (
        <div className="ew-layout">
          <EnvelopeSidebar
            envelopes={envelopes}
            selectedKey={selectedKey}
            detailsById={details}
            loading={listState.status === 'loading'}
            inputRef={inputRef}
            text={text}
            onText={setText}
            onSubmit={submitCreate}
            onClear={() => setText('')}
            createError={createError}
            readOnlyReason={readOnlyReason}
            onSelect={select}
            onRestore={restore}
            purge={purgePanel}
            dropFor={dropFor}
            dragging={Boolean(drag)}
          />
          <div className="ew-main">
            {listState.status === 'loading' ? <div className="ew-pane" data-testid="envelopes-pane-loading" aria-hidden="true"><ul className="ew-grid" data-view="grid">{[0, 1, 2, 3].map((n) => <li key={n} className="ew-tile ew-tile-skel"><span className="ew-skel ew-thumb-skel" /><span className="ew-skel ew-skel-line" /></li>)}</ul></div> : null}
            {noEnvelopes ? (
              <div className="ew-empty-card" data-testid="envelopes-empty">
                <h2>Envelopes</h2>
                <p>{ENVELOPES_TAB_COPY}</p>
                <p className="ew-muted">Name your first one in the box on the left and press Enter.</p>
              </div>
            ) : null}
            {listReady && selected && selectedDetails ? (
              <EnvelopePane
                orgId={orgId}
                envelope={selected}
                details={selectedDetails}
                totals={totals}
                filter={filter}
                onFilter={(patch) => setFilter((current) => ({ ...current, ...patch }))}
                view={view}
                onView={(next) => { setView(next); writeView(next); }}
                visible={visible}
                lockedReason={lockedReason}
                readOnlyReason={readOnlyReason}
                move={move}
                claimUi={claimUi}
                onRetry={retryDetail}
                onDetails={() => setDetailsOpen(true)}
                onStored={() => { loadedFor.current = ''; loadDetail(orgId, selectedId); }}
                onBusy={(on) => trackBusy('upload', on)}
                duplicateIds={duplicateReceiptIds(workspaceReceipts)}
                knownSha256s={workspaceReceipts.map((receipt) => receipt.sha256).filter(Boolean)}
                placeOf={(envelopeId) => duplicatePlace(envelopeId, envelopes)}
              />
            ) : null}
          </div>
        </div>
      ) : null}

      {narrow ? menuNode : null}
      <MoveToast toast={moveToast} onUndo={undoMove} onDismiss={dismissToast} />

      {openReceipt ? (
        <ReceiptDrawer
          key={openReceipt.receipt_document_id}
          orgId={orgId}
          envelopeId={selectedId}
          receipt={openReceipt}
          readOnly={Boolean(writeBlock) || selected?.status === 'deleted'}
          readOnlyReason={writeBlock || (selected?.status === 'deleted' ? 'This envelope is deleted — restore it to edit.' : '')}
          defaultCurrency={defaultCurrency}
          onClose={() => setOpenReceiptId(null)}
          onBusy={(on) => trackBusy('receipt-drawer', on)}
          onSaved={onReceiptSaved}
          onGone={onReceiptGone}
          onReadBack={async () => { await loadList(orgId, { silent: true }); return loadDetailRef.current(orgId, selectedId); }}
        />
      ) : null}
      {detailsOpen && selected && !selected.pending ? (
        <EnvelopeDetailsDrawer
          orgId={orgId}
          envelope={selected}
          readOnly={Boolean(readOnlyReason)}
          readOnlyReason={readOnlyReason}
          onClose={() => setDetailsOpen(false)}
          onBusy={(on) => trackBusy('details-drawer', on)}
          onRecord={applyRecord}
        />
      ) : null}
    </section>
  );
}

